import type { BootstrapData, Pin, PinComment, WidgetConfig } from '../types';

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

/**
 * Thin client for the public widget API. Every call is project-key scoped and
 * fails soft: network/⁠HTTP errors reject, callers decide how to degrade.
 */
export class ApiClient {
  constructor(private readonly config: WidgetConfig) {}

  async bootstrap(): Promise<BootstrapData> {
    return this.get<BootstrapData>('/widget/bootstrap');
  }

  async listPins(pageUrl: string): Promise<Pin[]> {
    return this.get<Pin[]>(`/widget/pins?page_url=${encodeURIComponent(pageUrl)}`);
  }

  async createPin(payload: Record<string, unknown>): Promise<Pin> {
    return this.post<Pin>('/widget/pins', payload);
  }

  async addComment(pinId: string, payload: Record<string, unknown>): Promise<PinComment> {
    return this.post<PinComment>(`/widget/pins/${pinId}/comments`, payload);
  }

  async uploadAttachment(pinId: string, file: Blob, filename: string): Promise<unknown> {
    const form = new FormData();
    form.append('file', file, filename);

    const headers: Record<string, string> = { 'X-Project-Key': this.config.projectKey, Accept: 'application/json' };
    if (this.config.sessionToken) headers['X-Feedback-Session'] = this.config.sessionToken;

    const res = await fetch(`${this.config.apiBase}/widget/pins/${pinId}/attachments`, {
      method: 'POST',
      headers,
      body: form,
    });
    if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
    return (await res.json()).data;
  }

  private async get<T>(path: string): Promise<T> {
    const res = await fetch(`${this.config.apiBase}${path}`, {
      headers: this.headers(),
    });
    return this.unwrap<T>(res);
  }

  private async post<T>(path: string, payload: Record<string, unknown>): Promise<T> {
    const res = await fetch(`${this.config.apiBase}${path}`, {
      method: 'POST',
      headers: { ...this.headers(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return this.unwrap<T>(res);
  }

  private headers(): Record<string, string> {
    const headers: Record<string, string> = {
      'X-Project-Key': this.config.projectKey,
      Accept: 'application/json',
    };
    if (this.config.sessionToken) headers['X-Feedback-Session'] = this.config.sessionToken;
    return headers;
  }

  private async unwrap<T>(res: Response): Promise<T> {
    if (!res.ok) {
      throw new Error(`Request failed: ${res.status}`);
    }
    const body = (await res.json()) as Envelope<T>;
    return body.data;
  }
}
